'use client'
import { useSession } from '@/shared/ui/providers/AuthSessionProvider'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { useGetMyInfosQuery } from '@/core/graphql/hooks'

import { useBooking } from '../../model/bookingContext'
import { BookingStepEnum } from '../../model/bookingTypes'
import { AuthenticationGate } from '../AuthenticationGate'
import { BookingStep } from './BookingStep'
import { FormSubmissionSection } from './FormSubmissionSection'
import { Summary } from './Summary'
import { IncompletedStepLabel } from './IncompletedStepLabel'
import { useGetMeetingLink } from '@/shared/api/meeting-link'
import { useSelectedAvailability } from '../../model/useSelectedAvailability'

export function FormSubmissionStep() {
  const { goToStep, isStepCompleted, state } = useBooking()
  const isCompleted = isStepCompleted(BookingStepEnum.FORM_SUBMISSION)

  if (state.currentStep === BookingStepEnum.FORM_SUBMISSION) {
    return (
      <BookingStep className="sm:!rounded-br-none">
        <BookingStep.Title>
          <Trans>Fill out the form</Trans>
        </BookingStep.Title>
        <BookingStep.Content>
          <FormSubmission meetingLinkSlug={state.meetingLinkSlug} />
        </BookingStep.Content>
      </BookingStep>
    )
  }

  if (isCompleted) {
    return <Summary icon="📝" label={t`Fill out the form`} onEdit={() => goToStep(BookingStepEnum.FORM_SUBMISSION)} text="" />
  }

  return (
    <IncompletedStepLabel icon="📝">
      <Trans>Fill out the form</Trans>
    </IncompletedStepLabel>
  )
}

function FormSubmission({ meetingLinkSlug }: { meetingLinkSlug: string }) {
  const { status } = useSession()
  const isLoggedIn = status === 'authenticated'
  const { state } = useBooking()

  // Get user info and meeting link data
  const { data: myInfosData, loading: isLoadingMyInfos } = useGetMyInfosQuery({ skip: !isLoggedIn })
  const { meetingLink, isLoadingMeetingLink } = useGetMeetingLink(meetingLinkSlug)

  const selectedAvailability = useSelectedAvailability(meetingLinkSlug)
  const formTemplate = selectedAvailability?.formTemplate

  // Handle form state preservation for authentication
  const handleFormStatePreservation = () => {
    if (typeof window !== 'undefined') {
      const stateKey = `bookingState_${meetingLink || 'default'}`
      try {
        localStorage.setItem(stateKey, JSON.stringify(state))
      } catch (error) {
        console.error('Error saving booking state:', error)
      }
    }
  }

  // Show loading state
  if (isLoadingMeetingLink || isLoadingMyInfos) return null

  // If not authenticated, show authentication gate
  if (!isLoggedIn || !myInfosData?.myInfos) {
    return (
      <AuthenticationGate returnPath={`/meeting-links/${meetingLinkSlug}/booking`} onAuthenticationRequired={handleFormStatePreservation}>
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-yellow-800">Please log in to continue with your form submission.</p>
        </div>
      </AuthenticationGate>
    )
  }

  // If we don't have a template, show an error
  if (!formTemplate) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-red-800 font-medium">Form template not found</h3>
          <p className="text-red-700 mt-2">The form template for this appointment type could not be found. Please try again or contact support.</p>
        </div>
      </div>
    )
  }

  // Render the form submission section
  return (
    <div className="max-sm:bg-white max-sm:p-4 max-sm:rounded-lg">
      <FormSubmissionSection template={formTemplate} meetingLink={meetingLinkSlug} patientId={myInfosData.myInfos._id} />
    </div>
  )
}
